import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '@strivelabs/ui-core/v2';
import { useAppStore } from '@/store/appStore';
import { useChatWorkflow } from '@/hooks/useChatWorkflow';
import { getChatWorkflowInstanceId } from '@/utils/featureFlags';
import { startChatHandoff } from '@/utils/chatHandoff';
import { logger } from '@/lib/logger';
import { ANALYTICS_EVENTS } from '@/lib/analytics/events';
import { useTrack } from '@/lib/analytics/useTrack';
import type { Task } from '@/types/task';

/**
 * Starts a conversation about one task.
 *
 * The task's id travels as platform context rather than only in the sentence,
 * because `task_update` takes the id and would otherwise have to go looking for
 * a task the reader is already pointing at.
 */
export const useOpenTaskInChat = () => {
  const navigate = useNavigate();
  const { mutateAsync: triggerChatWorkflow } = useChatWorkflow();
  const workflowInstanceId = useAppStore(
    (state) => getChatWorkflowInstanceId(state.agentInstances),
  );
  const trackEvent = useTrack();

  return useCallback(async (task: Task, via: 'new_chat' | 'chat' = 'chat') => {
    if (!workflowInstanceId) {
      toast.error('Chat is unavailable right now.');
      return;
    }

    trackEvent(ANALYTICS_EVENTS.TASK_OPENED_IN_CHAT, { via });

    try {
      await startChatHandoff({
        triggerChatWorkflow,
        navigate,
        workflowInstanceId,
        payload: {
          message: `Let's work on ${task.key}: ${task.title}`,
          platformContext: {
            type: 'task',
            task: { id: task.id, key: task.key, title: task.title },
          },
        },
      });
    } catch (error) {
      logger(error as Error, { context: 'useOpenTaskInChat: could not start the chat' });
      toast.error('Could not start that chat. Please try again.');
    }
  }, [ navigate, trackEvent, triggerChatWorkflow, workflowInstanceId ]);
};
